import { ccLabel } from 'owa-locstrings/lib/strings/cclabel.locstring.json';
import { fromLabel } from 'owa-locstrings/lib/strings/fromlabel.locstring.json';
import { onBehalfOfTextFormatString } from 'owa-locstrings/lib/strings/onbehalfoftextformatstring.locstring.json';
import {
    labelSeparator,
    sentLabel,
    toLabel,
    subjectLabel,
    whenLabel,
    whereLabel,
} from './createReplyForwardHeaderString.locstring.json';
import loc, { format } from 'owa-localize';
import { HTML_BLANK_LINE, PLAIN_TEXT_BLANK_LINE } from './constants';
import getFromAndSender from './getFromAndSender';
import getToAndCcRecipients from './getToAndCcRecipients';
import getWhenAndWhere from './getWhenAndWhere';
import { formatFullUserDateTime, userDate } from 'owa-datetime';
import encode from 'owa-encoding-utils/lib/utils/html/encode';
import ClientItem from 'owa-mail-store/lib/store/schema/ClientItem';
import Message from 'owa-service/lib/contract/Message';

const getDateTimeString = (date: string): string => {
    if (date) {
        return formatFullUserDateTime(userDate(date));
    }
    return '';
};

/**
 * Build header entries.
 */
const filterNullEntries = (entries: string[]): string[] => entries.filter(entry => !!entry);

/**
 * Build a header entry.
 */
const buildHeaderEntry = (name: string, value: string, isHtml: boolean): string =>
    isHtml
        ? `<b>${encode(name)}${loc(labelSeparator)}</b> ${encode(value)}`
        : `${name}${loc(labelSeparator)} ${value}`;

/**
 * Build reply/forward header into a string.
 */
const createReplyForwardHeaderString = (
    isHtml: boolean,
    originalMessage: Message,
    subject: string
): string => {
    const { from, sender, isSenderAddressDifferent } = getFromAndSender(originalMessage);
    const { toRecipients, ccRecipients } = getToAndCcRecipients(originalMessage);
    const { when, where } = getWhenAndWhere(originalMessage);

    // A message can be sent on behalf
    const fromWithDelegateInfo =
        from && sender && isSenderAddressDifferent
            ? format(loc(onBehalfOfTextFormatString), sender, from)
            : from;

    const dateTimeSent = originalMessage.DateTimeSent || originalMessage.DateTimeCreated;
    const clientItem = <ClientItem>originalMessage;
    const sent = dateTimeSent || clientItem.Smime?.RawDate;

    const fromLine = buildHeaderEntry(loc(fromLabel), fromWithDelegateInfo, isHtml);
    const sentLine = buildHeaderEntry(loc(sentLabel), getDateTimeString(sent), isHtml);
    const toLine = toRecipients
        ? buildHeaderEntry(loc(toLabel), toRecipients.join('; '), isHtml)
        : null;
    const ccLine = ccRecipients
        ? buildHeaderEntry(loc(ccLabel), ccRecipients.join('; '), isHtml)
        : null;
    const subjectLine = buildHeaderEntry(loc(subjectLabel), subject, isHtml);
    const whenLine = when ? buildHeaderEntry(loc(whenLabel), when, isHtml) : null;
    const whereLine = where ? buildHeaderEntry(loc(whereLabel), where, isHtml) : null;

    const headerEntries: string[] = filterNullEntries([
        fromLine,
        sentLine,
        toLine,
        ccLine,
        subjectLine,
        whenLine,
        whereLine,
    ]);

    if (isHtml) {
        // Header template like Outlook 2013
        return `<div style="font-family:Calibri,Arial,Helvetica,sans-serif; font-size:12pt; color:rgb(0,0,0);">
            <br/>
            <hr tabindex="-1" style="display:inline-block; width:98%;"/>
            ${headerEntries.join('<br/>')}
            ${HTML_BLANK_LINE}
            </div>`;
    } else {
        // Header template from Exchange 2013

        return `________________________________________${PLAIN_TEXT_BLANK_LINE}${headerEntries.join(
            PLAIN_TEXT_BLANK_LINE
        )}${PLAIN_TEXT_BLANK_LINE}${PLAIN_TEXT_BLANK_LINE}`;
    }
};

export default createReplyForwardHeaderString;
